import React, { useState, useEffect } from "react";
import { db, collection, onSnapshot, getDocs, fmt } from "../config/firebase";
import "./AdminComponents.css";

const AdminOverview = () => {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("today"); // "today" | "month" | "lifetime"
  const [bestsellerCategory, setBestsellerCategory] = useState("All");

  useEffect(() => {
    setLoading(true);
    const unsubscribe = onSnapshot(
      collection(db, "orders"),
      (snap) => {
        const items = [];
        snap.forEach((docSnap) => {
          items.push({ docId: docSnap.id, ...docSnap.data() });
        });
        setOrders(items);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching stats:", err);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  // Fetch products once to know each product's category
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const snap = await getDocs(collection(db, "products"));
      const items = [];
      snap.forEach((docSnap) => {
        items.push({ id: docSnap.id, ...docSnap.data() });
      });
      setProducts(items);
    } catch (err) {
      console.error("Error fetching products for bestsellers:", err);
    }
  };

  const getOrderDate = (order) => {
    if (order.createdAt?.toDate) return order.createdAt.toDate();
    if (order.createdAt?.seconds) return new Date(order.createdAt.seconds * 1000);
    if (typeof order.createdAt === "string") return new Date(order.createdAt);
    return null;
  };

  const filteredOrders = orders.filter((order) => {
    if (period === "lifetime") return true;
    const orderDate = getOrderDate(order);
    if (!orderDate) return false;

    const now = new Date();
    if (period === "today") {
      return (
        orderDate.getDate() === now.getDate() &&
        orderDate.getMonth() === now.getMonth() &&
        orderDate.getFullYear() === now.getFullYear()
      );
    }
    if (period === "month") {
      return (
        orderDate.getMonth() === now.getMonth() &&
        orderDate.getFullYear() === now.getFullYear()
      );
    }
    return true;
  });

  const stats = filteredOrders.reduce(
    (acc, order) => {
      acc.totalOrders++;
      acc.totalRevenue += order.advance || 0;
      if (order.status === "Pending" || order.status === "Paid") {
        acc.pendingOrders++;
      }
      return acc;
    },
    { totalOrders: 0, totalRevenue: 0, pendingOrders: 0 }
  );

  // Build a quick lookup: productId -> category
  const productCategoryMap = products.reduce((map, p) => {
    map[p.id] = p.category || "Uncategorized";
    return map;
  }, {});

  // Aggregate items across filteredOrders, keyed by productId + variantLabel
  // so different variants of the same product (e.g. Blue pen vs Red pen) are tracked separately
  const productStatsMap = {};
  filteredOrders.forEach((order) => {
    (order.items || []).forEach((item) => {
      const key = `${item.productId}__${item.variantLabel || "default"}`;
      if (!productStatsMap[key]) {
        productStatsMap[key] = {
          productId: item.productId,
          productName: item.productName,
          variantLabel: item.variantLabel || null,
          category: productCategoryMap[item.productId] || "Uncategorized",
          qty: 0,
          revenue: 0
        };
      }
      productStatsMap[key].qty += item.quantity || 0;
      productStatsMap[key].revenue += (item.price || 0) * (item.quantity || 0);
    });
  });

  let bestsellerList = Object.values(productStatsMap);

  const bestsellerCategories = [
    "All",
    ...new Set(bestsellerList.map((p) => p.category))
  ];

  if (bestsellerCategory !== "All") {
    bestsellerList = bestsellerList.filter((p) => p.category === bestsellerCategory);
  }

  bestsellerList.sort((a, b) => b.qty - a.qty);
  const topProducts = bestsellerList.slice(0, 10);

  const periods = [
    { id: "today", label: "Today" },
    { id: "month", label: "This Month" },
    { id: "lifetime", label: "Lifetime" }
  ];

  return (
    <div className="admin-overview">
      <div className="admin-header-row">
        <h2 className="admin-section-title">Dashboard Overview</h2>
      </div>

      <div className="period-tabs">
        {periods.map((p) => (
          <button
            key={p.id}
            className={`period-tab ${period === p.id ? "active" : ""}`}
            onClick={() => setPeriod(p.id)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="admin-loading">
          <div className="spinner"></div>
        </div>
      ) : (
        <>
          <div className="stats-grid">
            <div className="stat-card stat-orders">
              <div className="stat-icon">📦</div>
              <div className="stat-value">{stats.totalOrders}</div>
              <div className="stat-label">
                {period === "today" ? "Orders Today" : period === "month" ? "Orders This Month" : "Total Orders"}
              </div>
            </div>
            <div className="stat-card stat-revenue">
              <div className="stat-icon">₹</div>
              <div className="stat-value">{fmt(stats.totalRevenue)}</div>
              <div className="stat-label">
                {period === "today" ? "Received Today" : period === "month" ? "Received This Month" : "Total Received"}
              </div>
            </div>
            <div className="stat-card stat-pending">
              <div className="stat-icon">⏳</div>
              <div className="stat-value">{stats.pendingOrders}</div>
              <div className="stat-label">Pending Orders</div>
            </div>
          </div>

          <div className="bestsellers-section">
            <div className="admin-header-row">
              <h3 className="admin-subsection-title">Top Products</h3>
            </div>

            {bestsellerCategories.length > 1 && (
              <div className="category-tabs">
                {bestsellerCategories.map((cat) => (
                  <button
                    key={cat}
                    className={`category-tab ${bestsellerCategory === cat ? "active" : ""}`}
                    onClick={() => setBestsellerCategory(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}

            {topProducts.length === 0 ? (
              <div className="admin-empty">No sales data for this period yet.</div>
            ) : (
              <div className="orders-table-wrapper">
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Product</th>
                      <th>Category</th>
                      <th>Units Sold</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topProducts.map((p, idx) => (
                      <tr key={`${p.productId}-${p.variantLabel || "default"}`}>
                        <td>{idx + 1}</td>
                        <td className="product-cell-name">
                          {p.productName}
                          {p.variantLabel && <span className="bestseller-variant"> ({p.variantLabel})</span>}
                        </td>
                        <td>{p.category}</td>
                        <td>{p.qty}</td>
                        <td className="order-total">{fmt(p.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AdminOverview;